import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';
import {MessageService} from '../service-ui/message.service';
import {StartupService} from '../service-ui/startup.service';
import {environment} from '../../environments/environment';
import {DataService} from './data-service';
import {CollectionProvider} from '../../model/CollectionProvider';

/**
 * Client access to the server's CollectionProvider CRUD API.
 *
 * Server contract: `docs/contracts/shared/CollectionProvider.md`.
 * Routes: `GET/POST/PUT/DELETE /1.0/ui/collectionprovider[/:id]`.
 *
 * Authorization is enforced on the server side (`network` = full CRUD;
 * `serviceProvider` = read/update only its mapped provider); the
 * client just relays the caller's role via the shared
 * `StartupService.getHttpOptions()` and surfaces server errors.
 *
 * Pattern mirrors {@link ServiceProviderService}: object cache primed
 * on first list call, per-id GET returns a freshly-parsed
 * {@link CollectionProvider} without touching the cache (so the caller
 * gets the latest server state whenever they explicitly ask). This is
 * NOT an `AbstractLiveDataService` because there's no notification
 * plumbing needed — CollectionProvider mutations happen only from
 * these two settings pages and the fresh-fetch-on-open flow is
 * sufficient.
 */
@Injectable()
export class CollectionProviderService extends DataService {
  private objects: CollectionProvider[] = [];
  private objectsObservable = of(this.objects);
  public loadingState = 'INITIAL';

  constructor(private http: HttpClient,
              private messageService: MessageService,
              private startupService: StartupService) {
    super(startupService);
  }

  reset(filter: string = '') {
    this.loadingState = 'INITIAL';
    while (this.objects.length > 0) { this.objects.pop(); }
  }

  /** GET all CollectionProviders (network role only; SP role gets a 403 from the server). */
  getCollectionProviders(): Observable<CollectionProvider[]> {
    if (this.loadingState === 'INITIAL') {
      this.loadingState = 'LOADING';
      const url = `${environment.apiBaseURL}collectionprovider`;
      this.messageService.add('CollectionProviderService: fetched collectionProviders');
      return this.http.get<CollectionProvider[]>(url, this.startupService.getHttpOptions())
        .pipe(
          map(collectionProviders => {
            while (this.objects.length > 0) { this.objects.pop(); }
            for (const cp of collectionProviders || []) {
              this.objects.push(new CollectionProvider(cp));
            }
            this.loadingState = 'LOADED';
            return this.objects;
          }),
          catchError(this.handleError('getCollectionProviders', []))
        );
    }
    return this.objectsObservable;
  }

  /** GET one CollectionProvider. Always fetches fresh (bypasses the local cache). */
  getCollectionProvider(id: string | number): Observable<CollectionProvider> {
    const url = `${environment.apiBaseURL}collectionprovider/${id}`;
    this.messageService.add(`CollectionProviderService: fetched collectionProvider id=${id}`);
    return this.http.get<CollectionProvider>(url, this.startupService.getHttpOptions()).pipe(
      map(cp => new CollectionProvider(cp)),
      catchError(this.handleError<CollectionProvider>(`getCollectionProvider id=${id}`))
    );
  }

  /** PUT: update the CollectionProvider on the server. */
  updateCollectionProvider(cp: CollectionProvider): Observable<CollectionProvider> {
    const url = `${environment.apiBaseURL}collectionprovider/${cp.ID}`;
    this.messageService.add(`CollectionProviderService: updated collectionProvider id=${cp.ID}`);
    return this.http.put<CollectionProvider>(url, cp, this.startupService.getHttpOptions()).pipe(
      tap(_ => this.log(`updated collectionProvider id=${cp.ID}`)),
      catchError(this.handleError<CollectionProvider>('updateCollectionProvider'))
    );
  }

  /** POST: create a new CollectionProvider. */
  addCollectionProvider(cp: CollectionProvider): Observable<CollectionProvider> {
    const url = `${environment.apiBaseURL}collectionprovider`;
    return this.http.post<CollectionProvider>(url, cp, this.startupService.getHttpOptions()).pipe(
      tap(o => {
        this.log(`added collectionProvider w/ id=${o && o.ID}`);
        if (o) { this.objects.push(new CollectionProvider(o)); }
      }),
      catchError(this.handleError<CollectionProvider>('addCollectionProvider'))
    );
  }

  /** DELETE: delete a CollectionProvider from the server. */
  deleteCollectionProvider(cp: CollectionProvider): Observable<CollectionProvider> {
    const url = `${environment.apiBaseURL}collectionprovider/${cp.ID}`;
    return this.http.delete<CollectionProvider>(url, this.startupService.getHttpOptions()).pipe(
      tap(_ => this.log(`deleted collectionProvider id=${cp.ID}`)),
      catchError(this.handleError<CollectionProvider>('deleteCollectionProvider'))
    );
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      this.loadingState = 'INITIAL';
      console.error(error, operation);
      this.log(`${operation} failed: ${error && error.message}`);
      return of(result as T);
    };
  }

  private log(message: string) {
    this.messageService.add('CollectionProviderService: ' + message);
  }
}

